import React, { useEffect } from "react";
import "./App.scss";
import { Routes, Route, useLocation } from "react-router-dom";
// import ReactGA from 'react-ga4';

import Splash from "./pages/Splash/Splash";
import SellMyHome from "./pages/SellMyHome/SellMyHome";
import SellerStories from "./pages/SellerStories/SellerStories";
import About from "./pages/About/About";
import AvailableHomes from "./pages/AvailableHomes/AvailableHomes";
import FAQs from "./pages/FAQs/FAQs";
import Contact from "./pages/Contact/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import ThankYou from "./components/ThankYou/ThankYou";

// import { useAnalytics } from "./customHooks/reactGA";
import CallRailEmbed from "./customHooks/callRail";
import storeTrackingParams from "./utils/storeTrackingParams";

import { ThemeProvider, useTheme, createTheme, Box } from "@mui/material";
const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
});

function App(props) {
  const theme = useTheme();
  const colorMode = React.useContext(ColorModeContext);

  const location = useLocation();
  const lastMetaPage = React.useRef(location.pathname + location.search);

  useEffect(() => {
    const sendPageView = () => {
      if (!window.gtag) return;
      window.gtag("config", "AW-17688518709", {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    };

    sendPageView();

    // if gtag hasn't loaded yet, try once shortly after
    const t = setTimeout(sendPageView, 500);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const currentPage = location.pathname + location.search;

    if (currentPage === lastMetaPage.current) return;

    lastMetaPage.current = currentPage;
    window.fbq?.("track", "PageView");
  }, [location.pathname, location.search]);

  useEffect(() => {
    storeTrackingParams();
  }, []);

  return (
    <div className="App">
      <CallRailEmbed />
      <Box
        sx={{
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.primary.main
              : theme.palette.background.default,
        }}
      >
        <Routes>
          <Route
            index
            element={<Splash colorMode={colorMode} theme={theme} />}
          />
          <Route
            path="sell-my-home"
            element={<SellMyHome colorMode={colorMode} theme={theme} />}
          />
          <Route
            path="about"
            element={<About colorMode={colorMode} theme={theme} />}
          />
          <Route
            path="available-homes"
            element={<AvailableHomes colorMode={colorMode} theme={theme} />}
          />
          <Route
            path="seller-stories"
            element={<SellerStories colorMode={colorMode} theme={theme} />}
          />
          <Route
            path="frequently-asked-questions"
            element={<FAQs colorMode={colorMode} theme={theme} />}
          />
          <Route
            path="contact"
            element={<Contact colorMode={colorMode} theme={theme} />}
          />
          <Route
            path="privacy-policy"
            element={<PrivacyPolicy colorMode={colorMode} theme={theme} />}
          />
          <Route path="/thank-you" element={<ThankYou theme={theme} />} />
        </Routes>
      </Box>
    </div>
  );
}

export default function ToggleColorMode() {
  const [mode, setMode] = React.useState(() => {
    return localStorage.getItem("theme") || "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    localStorage.setItem("theme", mode);
  }, [mode]);

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    [],
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          common: {
            black: "#000",
            white: "#fff",
          },
          primary: {
            main: "#1a2038",
            light: "#202845",
            dark: "#14192b",
            // contrastText: '',
            ...(mode === "dark" && {
              main: "#1a2038",
              // light: '',
              // dark: '',
              // contrastText: '',
            }),
          },
          secondary: {
            main: "#abc3b5",
            light: "#b8d1c2",
            dark: "#a0b6a9",
            // contrastText: '',
          },
          error: {
            main: "#1a2038",
            // light: '',
            // dark: '',
            // contrastText: '',
          },
          warning: {
            main: "#c76119",
            // light: '',
            // dark: '',
            // contrastText: '',
          },
          info: {
            main: "#1a3553",
            light: "#1e3d60",
            dark: "#162d46",
            // contrastText: '',
          },
          success: {
            main: "#d8b57b",
            light: "#e5c083",
            dark: "#c9a973",
            // contrastText: '',
          },
          background: {
            paper: "#fff",
            default: "#fff",
          },
          action: {
            active: "rgba(0, 0, 0, 0.54)",
            hover: "rgba(0, 0, 0, 0.04)",
            hoverOpacity: "0.04",
            selected: "rgba(0, 0, 0, 0.08)",
            selectedOpacity: "0.08",
            disabled: "rgba(0, 0, 0, 0.26)",
            disabledBackground: "rgba(0, 0, 0, 0.12)",
            disabledOpacity: "0.38",
            focus: "rgba(0, 0, 0, 0.12)",
            focusOpacity: "0.12",
            activatedOpacity: "0.12",
          },
        },
        typography: {
          htmlFontSize: 16,
          fontFamily: "latoRegular",
          fontSize: 16,
        },
      }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <App theme={theme} />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
